<template>
  <div class="bg-gradient-to-b from-red-50 to-white">
    <div class="container mx-auto px-4 py-8">
      <div class="max-w-3xl mx-auto">
        <!-- Back navigation -->
        <router-link to="/blog" class="group flex items-center text-blue-600 mb-8 transition-colors hover:text-blue-800">
          <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5 mr-2 transition-transform group-hover:-translate-x-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M10 19l-7-7m0 0l7-7m-7 7h18" />
          </svg>
          <span class="font-medium">Back to Blog</span>
        </router-link>
        
        <!-- Post header -->
        <div class="mb-8">
          <h1 class="text-4xl font-extrabold mb-4 leading-tight text-gray-900">{{ title }}</h1>
          <div class="flex items-center text-gray-600 text-sm mb-6">
            <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
            </svg>
            <span>Published: {{ published }} | Last Updated: {{ updated }}</span>
          </div>
        </div>
        
        <!-- Featured image -->
        <div class="relative mb-10 shadow-xl rounded-lg overflow-hidden">
          <img :src="image" :alt="imageAlt" class="w-full h-80 object-cover">
          <div class="absolute inset-0 bg-gradient-to-t from-black/40 to-transparent"></div>
        </div>
        
        <!-- Post content -->
        <div class="prose prose-lg max-w-none">
          <slot></slot>
        </div>
        
        <!-- CTA Section -->
        <div class="mt-12 p-6 bg-gradient-to-r from-blue-100 to-blue-50 rounded-lg shadow-md">
          <h3 class="font-bold text-xl mb-3 text-gray-900">{{ ctaTitle }}</h3>
          <p class="mb-6 text-gray-700">{{ ctaText }}</p>
          <router-link to="/register" class="inline-flex items-center px-6 py-3 bg-blue-600 text-white font-medium rounded-md shadow-sm hover:bg-blue-700 transition-colors duration-200">
            Sign Up for Free
            <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5 ml-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 7l5 5m0 0l-5 5m5-5H6" />
            </svg>
          </router-link>
        </div>
        
        <!-- Related posts -->
        <div class="mt-12 pt-8 border-t border-gray-200">
          <h3 class="text-2xl font-bold mb-6">Related Articles</h3>
          <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div v-for="(post, i) in relatedPosts" :key="i" class="bg-white rounded-lg shadow-md overflow-hidden transition-transform hover:scale-[1.02]">
              <router-link :to="post.path" class="block h-full">
                <img :src="post.image" :alt="post.title" class="w-full h-40 object-cover">
                <div class="p-4">
                  <h3 class="text-lg font-semibold mb-2 text-gray-900">{{ post.title }}</h3>
                  <span class="text-blue-600 text-sm font-medium">Read more →</span>
                </div>
              </router-link>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: "BlogPostTemplate",
  props: {
    title: {
      type: String,
      required: true
    },
    published: {
      type: String,
      required: true
    },
    updated: {
      type: String,
      required: true
    },
    image: {
      type: String,
      required: true
    },
    imageAlt: {
      type: String,
      required: true
    },
    ctaTitle: {
      type: String,
      required: true
    },
    ctaText: {
      type: String,
      required: true
    },
    relatedPosts: {
      type: Array,
      default: () => []
    }
  }
};
</script>

<style>
.prose h2 {
  @apply text-2xl font-bold mt-10 mb-4 text-gray-900 border-b border-gray-200 pb-2;
}

.prose h3 {
  @apply text-xl font-semibold mt-8 mb-3 text-gray-900;
}

.prose p {
  @apply my-4 leading-relaxed text-gray-700;
}

.prose ul {
  @apply my-6 pl-6 space-y-2;
}

.prose li {
  @apply text-gray-700 leading-relaxed;
}

.prose strong {
  @apply font-semibold text-gray-900;
}

.prose a {
  @apply text-blue-600 hover:text-blue-800 underline;
}
</style> 